import type { DataflowSummary } from "@shared/types/dataflows";
import { readsAgainAfterMove } from "@shared/runs/destinationMove";

/**
 * Round 4 item 7a: the one sentence every incremental or full choice carries (Run now's Full
 * refresh, and the schedule's per-connection mode).
 *
 * An incremental import reads only what changed since the last import, so until one import has
 * landed there is nothing to read changes from, and the runner reads everything once
 * (`runner/config/bundle.ts` `refreshPlanFor`, recorded on the run as `refreshFallback`). The
 * choice is always offered (owner decision, round 4): hiding it until a first full import would
 * make a control that appears by state and could drift from the runner's per-table rule.
 */
export const FIRST_IMPORT_HINT = "The first import reads everything.";

/** #984: said beside Full refresh once the connection has imported, where "first import" is no longer true. */
export const AFTER_FIRST_IMPORT_HINT =
  "Without Full refresh, only changes since the last import are read.";

/**
 * #1077: the same, for a schedule's Run now when some of its connections import every row
 * (`everyRowSentence` names them), so the hint covers only the ones that import incrementally.
 */
export const AFTER_FIRST_IMPORT_HINT_SOME =
  "Without Full refresh, the connections that import incrementally read only changes since their last import.";

/**
 * #984: a schedule row's Incremental help once its connection has imported (`hasImported`), where
 * "The first import reads everything" is no longer true. Before that the row keeps the owner's
 * full sentence (`ROW_REFRESH_HELP`, owner 2026-10-01).
 */
export const ROW_INCREMENTAL_HELP_AFTER_IMPORT = "Reads only what changed since the last import.";

/**
 * #1077: under a schedule's "Connections to import in full". Only the incremental connections are
 * listed, so the sentence is about the listed ones left unticked, never the schedule's others.
 */
export const UNTICKED_CONNECTIONS_NOTE =
  "Connections left unticked import only what changed since their last import. A connection's first import reads everything either way.";

/** #984: the same note once every listed connection has imported, where "first import" is no longer true. */
export const UNTICKED_CONNECTIONS_NOTE_AFTER_IMPORT =
  "Connections left unticked import only what changed since their last import.";

/**
 * #1077: a schedule's Run now names its connections that import every row each time (a connector
 * that cannot import incrementally, or a connection that imports in full), so no sentence about
 * "only changes" reads as covering them.
 */
export function everyRowSentence(names: readonly string[]): string | null {
  if (names.length === 0) return null;
  if (names.length === 1) return `${names[0]} imports every row each time.`;
  const list = `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `${list} import every row each time.`;
}

/**
 * #1077: said wherever a connection's connector cannot import incrementally (`isIncrementalSupported`
 * false, SQL Server among them): the schedule row's read-only Full, Run now in place of Full
 * refresh, and the connection editor's Refresh line. Such a connection may still store
 * `incremental` (the server keeps what is stored, and reports `full` for it), but its tables carry
 * no watermark, so the runner sends no `incrementalColumn` and the extract reads every row
 * (`runner/config/bundle.ts` `bundleEntity`); there is nothing a Full refresh would change.
 */
export const FULL_ONLY_IMPORT_NOTE =
  "This connector imports every row each time. Incremental isn't supported.";

/**
 * #984: every selected table of the connection has a bound. `lastRefreshAt` is the EARLIEST bound,
 * so it is null while any table has none, even after a succeeded last run or a landed table:
 * those tables still read everything (`firstImportNote`), so "only changes are read" would be false.
 */
export function hasImported(connection: Pick<DataflowSummary, "lastRefreshAt">): boolean {
  return connection.lastRefreshAt != null;
}

/**
 * #2521: beside Full refresh when each connection not yet imported again only lost its bounds to a
 * confirmed destination move (`readsAgainAfterMove`), where "The first import" would be untrue.
 */
export const MOVED_IMPORT_HINT =
  "The next import reads everything again, because the destination moved.";

/**
 * #2521: beside Full refresh when some connections lost their bounds to a destination move and
 * the others have imported, so the sentence is true of each.
 */
export const MOVED_MIXED_IMPORT_HINT =
  "Without Full refresh, a connection whose destination moved reads everything again, and the others read only changes since their last import.";

/** The sentence beside Full refresh for these connections: the first-import one until all have imported. */
export function importHintFor(
  connections: ReadonlyArray<
    Pick<DataflowSummary, "lastRefreshAt"> &
      Partial<Pick<DataflowSummary, "hasLanded" | "boundsResetAt">>
  >,
): string {
  if (connections.length === 0) return FIRST_IMPORT_HINT;
  if (connections.every(hasImported)) return AFTER_FIRST_IMPORT_HINT;
  if (connections.every(readsAgainAfterMove)) return MOVED_IMPORT_HINT;
  return connections.every((row) => hasImported(row) || readsAgainAfterMove(row))
    ? MOVED_MIXED_IMPORT_HINT
    : FIRST_IMPORT_HINT;
}

/** True for the hints a destination move chooses (`importHintFor`, and its whole-days twin). */
export function isMovedImportHint(hint: string): boolean {
  return (
    hint === MOVED_IMPORT_HINT ||
    hint === MOVED_MIXED_IMPORT_HINT ||
    hint === MOVED_MIXED_IMPORT_HINT_WHOLE_DAYS
  );
}

/**
 * Round 6, S2: the connection editor's read-only line where the Refresh radio was. The trigger
 * decides the mode now (each schedule row, and Run now), so the connection says only that it
 * imports incrementally where its connector can.
 */
export const CONNECTION_REFRESH_LINE =
  "Imports incrementally where it can. Choose Full on a schedule, or Full refresh in Run now, to bring every row again.";

/** The sentence when some of a connection's tables have landed and others have not. */
export const SOME_TABLES_NOT_IMPORTED =
  "Some of its tables have not been imported yet. Those read everything on the next run.";

/**
 * #2521: the one rule for "reads everything again because the destination moved", shared with
 * the MCP tools (`shared/runs/destinationMove.ts`).
 */
export { readsAgainAfterMove } from "@shared/runs/destinationMove";

/**
 * Round 6, S3 (owner, 2026-10-01, asked for Incremental to be disabled until a full import
 * succeeds; agreed instead: a note). The runner already reads a table whole while it has no bound
 * (`refreshPlanFor`, `fellBackToFull`), so an incremental first run is a full run, and a disabled
 * control would protect nothing and leave forgotten schedules running full imports for ever.
 *
 * `lastRefreshAt` is the EARLIEST bound over the selected tables, so it is null while any table
 * has none: after a run that landed, that means some tables, not the connection, are still new.
 * Null when every selected table has landed. Round 7: a connection that landed before
 * (`hasLanded`) says the same about its new tables, even after a failed last run.
 */
export function firstImportNote(
  connection: Pick<
    DataflowSummary,
    "name" | "displayName" | "lastRefreshAt" | "lastRun" | "hasLanded" | "boundsResetAt"
  >,
  /** #1238: its connector re-reads whole days, so later runs do not read "only what changed". */
  wholeDays = false,
): string | null {
  if (connection.lastRefreshAt !== null) return null;
  const later = wholeDays
    ? WHOLE_DAYS_LATER_SENTENCE.replace("After that, daily", "Later runs: daily")
    : "Later runs read only what changed.";
  // #2521: before the outcome, since the last run listed may be one from before the move.
  if (readsAgainAfterMove(connection)) {
    return `${connection.displayName} reads everything again on its next import, because the destination moved. ${later}`;
  }
  const outcome = connection.lastRun
    ? (connection.lastRun.outcome ?? connection.lastRun.status)
    : null;
  if (outcome === "succeeded" || outcome === "partially_succeeded") return SOME_TABLES_NOT_IMPORTED;
  // Round 7: it landed before and only gained a table, whatever its last run did.
  if (connection.hasLanded === true) return SOME_TABLES_NOT_IMPORTED;
  return `${connection.displayName} has no successful import yet. Its first run reads everything. ${later}`;
}

/**
 * #1238: a connector that re-reads whole days (`ConnectorSummary.readsWholeDays`, Mouseflow) does
 * not read "only changes". Its daily tables re-read the lookback days before the last import date
 * and then every day to yesterday (`runner/config/bundle.ts` `goBackDays`), and today arrives with
 * the next day's import. Its other tables are wiped and loaded whole, so the sentence says the
 * split rather than one rule for the connector (the flag is per connector, not per table).
 */
const DAYS_CLAUSE = "every day from the last import date, less the lookback days, up to yesterday";
const OTHER_TABLES_CLAUSE = "The other tables are read whole each time.";

/**
 * #2521: a schedule row's Incremental help while its connection reads everything again because a
 * confirmed destination move cleared its bounds (`readsAgainAfterMove`).
 */
export const ROW_INCREMENTAL_HELP_AFTER_MOVE =
  "Reads only what changed since the last import. The next import reads everything, because the destination moved.";

/** Said where the connection has imported before. */
export const WHOLE_DAYS_SENTENCE = `Daily tables read ${DAYS_CLAUSE}. ${OTHER_TABLES_CLAUSE}`;

/** Said where the connection has not imported yet, or a first import is still possible. */
export const WHOLE_DAYS_LATER_SENTENCE = `After that, daily tables read ${DAYS_CLAUSE}. ${OTHER_TABLES_CLAUSE}`;

/** The connection editor's Refresh line for such a connector. */
export const CONNECTION_REFRESH_LINE_WHOLE_DAYS = `Imports incrementally where it can. ${WHOLE_DAYS_SENTENCE} Choose Full on a schedule, or Full refresh in Run now, to bring every row again.`;

/** #2521: the same while a whole-days connection reads everything again after a destination move. */
export const ROW_INCREMENTAL_HELP_WHOLE_DAYS_AFTER_MOVE = `The next import reads everything, because the destination moved. ${WHOLE_DAYS_LATER_SENTENCE}`;

/** A schedule row's Incremental help for such a connection before its first import (after it, `WHOLE_DAYS_SENTENCE`). */
export const ROW_INCREMENTAL_HELP_WHOLE_DAYS = `The first import reads everything, because there is nothing to compare with yet. ${WHOLE_DAYS_LATER_SENTENCE}`;

/** Beside Run now's Full refresh once the connections have imported, when all read whole days. */
export const AFTER_FIRST_IMPORT_HINT_WHOLE_DAYS = `Without Full refresh, daily tables read ${DAYS_CLAUSE}. ${OTHER_TABLES_CLAUSE}`;

/**
 * #2521 with #1238: the moved-and-imported hint when a connection that has imported reads whole
 * days, so "only changes" never covers it.
 */
export const MOVED_MIXED_IMPORT_HINT_WHOLE_DAYS = `Without Full refresh, a connection whose destination moved reads everything again. Of the others, a connection that reads whole days reads its daily tables ${DAYS_CLAUSE}, and its other tables whole, and the rest read only changes since their last import.`;

/** The same where only some of the connections read whole days. */
export const AFTER_FIRST_IMPORT_HINT_MIXED = `Without Full refresh, a connection that reads whole days reads its daily tables ${DAYS_CLAUSE}, and its other tables whole. The other connections that import incrementally read only changes since their last import.`;

/** Under "Connections to import in full" when a listed connection reads whole days. */
export const UNTICKED_CONNECTIONS_NOTE_WHOLE_DAYS = `Connections left unticked import only what changed since their last import, except a connection that reads whole days. Its daily tables read ${DAYS_CLAUSE}, and its other tables are read whole. A connection's first import reads everything either way.`;

/** #2521: under "Connections to import in full" while a listed connection's destination moved. */
export const UNTICKED_CONNECTIONS_NOTE_AFTER_MOVE =
  "Connections left unticked import only what changed since their last import. A connection whose destination moved reads everything again either way.";

/** #2521: the same when a listed connection reads whole days. */
export const UNTICKED_CONNECTIONS_NOTE_WHOLE_DAYS_AFTER_MOVE = `Connections left unticked import only what changed since their last import, except a connection that reads whole days. Its daily tables read ${DAYS_CLAUSE}, and its other tables are read whole. A connection whose destination moved reads everything again either way.`;

/** The same once every listed connection has imported, where "first import" is no longer true. */
export const UNTICKED_CONNECTIONS_NOTE_WHOLE_DAYS_AFTER_IMPORT = `Connections left unticked import only what changed since their last import, except a connection that reads whole days. Its daily tables read ${DAYS_CLAUSE}, and its other tables are read whole.`;

/** True when the connector of `connection` is listed and reads whole days. */
export function readsWholeDays(
  connection: Pick<DataflowSummary, "connectorName">,
  connectors: ReadonlyMap<string, { readsWholeDays?: boolean }>,
): boolean {
  return connectors.get(connection.connectorName)?.readsWholeDays === true;
}
